import React from 'react';
import { getBannerLayoutMetrics } from '../utils/doctorProfile';

type PdfHeaderPreviewProps = {
  backgroundSrc: string;
  compact?: boolean;
  bannerConfig: {
    title?: string;
    subtitle?: string;
    doctorName?: string;
    displayName?: string;
    specialty?: string;
    hospital?: string;
    phone?: string;
    email?: string;
    site?: string;
    textColor?: string;
    backgroundStyle?: string;
    backgroundSpec?: string;
    layoutPreset?: 'compact' | 'balanced' | 'spacious';
    layoutVariant?: string;
    layoutSpec?: string;
    leftOffsetX?: number;
    leftOffsetY?: number;
    rightOffsetX?: number;
    rightOffsetY?: number;
    bannerOffsetLeftX?: number;
    bannerOffsetLeftY?: number;
    bannerOffsetRightX?: number;
    bannerOffsetRightY?: number;
    bannerLayoutPreset?: 'compact' | 'balanced' | 'spacious';
    bannerLayoutVariant?: string;
    bannerLayoutSpec?: string;
    bannerOffsetLeft?: number;
    bannerOffsetRight?: number;
    bannerMode?: string;
  };
};

const isHexColor = (value?: string) => /^#([0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(String(value || '').trim());
const HEADER_PREVIEW_WIDTH = 1600;
const HEADER_PREVIEW_HEIGHT = 320;

const PdfHeaderPreview: React.FC<PdfHeaderPreviewProps> = ({ backgroundSrc, compact = false, bannerConfig }) => {
  const hasBannerImage = Boolean(String(backgroundSrc || '').trim());
  const isLightBanner = String(bannerConfig.backgroundStyle || bannerConfig.backgroundSpec || '').toLowerCase().includes('light');
  const useExplicitTextColor = isHexColor(bannerConfig.textColor);

  const textColor = useExplicitTextColor ? bannerConfig.textColor : isLightBanner ? '#0F172A' : '#FFFFFF';
  const secondaryColor = useExplicitTextColor
    ? bannerConfig.textColor
    : isLightBanner
      ? '#334155'
      : '#E2E8F0';
  const accentColor = useExplicitTextColor
    ? bannerConfig.textColor
    : isLightBanner
      ? '#1E293B'
      : '#BAE6FD';

  const previewLayout = getBannerLayoutMetrics(
    {
      ...bannerConfig,
      bannerImageUrl: backgroundSrc || '',
      layoutPreset: bannerConfig.bannerLayoutPreset || bannerConfig.layoutPreset || 'balanced',
      layoutVariant: bannerConfig.bannerLayoutVariant || bannerConfig.layoutVariant || bannerConfig.layoutSpec || 'balanced',
      layoutSpec: bannerConfig.bannerLayoutSpec || bannerConfig.layoutSpec || bannerConfig.layoutVariant || 'balanced',
      leftOffsetX: Number(bannerConfig.bannerOffsetLeftX ?? bannerConfig.leftOffsetX ?? 0),
      leftOffsetY: Number(bannerConfig.bannerOffsetLeftY ?? bannerConfig.leftOffsetY ?? 0),
      rightOffsetX: Number(bannerConfig.bannerOffsetRightX ?? bannerConfig.rightOffsetX ?? 0),
      rightOffsetY: Number(bannerConfig.bannerOffsetRightY ?? bannerConfig.rightOffsetY ?? 0),
    } as any,
    HEADER_PREVIEW_WIDTH,
    HEADER_PREVIEW_HEIGHT
  );

  const textShadow = isLightBanner ? '0 1px 2px rgba(255,255,255,0.35)' : '0 2px 10px rgba(0,0,0,0.40)';
  const leftTextClass = compact ? 'truncate' : 'whitespace-normal break-words';
  const rightTextClass = compact ? 'truncate' : 'whitespace-normal break-words';
  const rightContactClass = compact ? 'truncate' : 'whitespace-normal break-words';
  const leftSubtitleSpacing = compact ? 1.5 : 1.5;
  const rightLineSpacing = compact ? 4 : 5;

  const renderOverlay = () => (
    <div className="absolute inset-0 z-10">
      <div className="absolute left-0 top-0 h-full w-full">
        <div
          className="absolute h-8 w-8 rounded-xl border border-white/20 bg-white/10 backdrop-blur-[2px]"
          style={{
            left: previewLayout.leftX,
            top: Math.max(10, previewLayout.leftY - 8),
          }}
        />

        <div
          className="absolute min-w-0"
          style={{
            left: previewLayout.leftX,
            top: previewLayout.leftY + 8,
            width: previewLayout.leftWidth,
            maxWidth: previewLayout.leftWidth,
          }}
        >
          <p
            className={`max-w-full font-black leading-[0.92] ${leftTextClass}`}
            style={{
              color: textColor,
              textShadow,
              fontSize: previewLayout.titleFontSize,
              letterSpacing: '0.12em',
            }}
          >
            {bannerConfig.title || 'ORTHO PRECISION'}
          </p>
          <p
            className={`max-w-full font-semibold uppercase ${leftTextClass}`}
            style={{
              color: accentColor,
              textShadow,
              fontSize: previewLayout.subtitleFontSize,
              letterSpacing: '0.36em',
              marginTop: leftSubtitleSpacing,
            }}
          >
            {bannerConfig.subtitle || 'MEDICAL CREDENTIAL'}
          </p>
        </div>

        <div
          className="absolute min-w-0 text-right"
          style={{
            left: previewLayout.rightX,
            top: previewLayout.rightY + 8,
            width: previewLayout.rightWidth,
            maxWidth: previewLayout.rightWidth,
          }}
        >
          <p
            className={`max-w-full font-bold leading-tight ${rightTextClass}`}
            style={{
              color: textColor,
              textShadow,
              fontSize: previewLayout.displayNameFontSize * 1.08,
            }}
          >
            {bannerConfig.hospital || 'Clínica / Hospital'}
          </p>
          <p
            className={`mt-1 max-w-full font-semibold leading-tight ${rightTextClass}`}
            style={{
              color: textColor,
              textShadow,
              fontSize: previewLayout.displayNameFontSize * 0.98,
            }}
          >
            {bannerConfig.displayName || bannerConfig.doctorName || 'Médico Responsável'}
          </p>
          <div
            className="mt-2 flex max-w-full flex-col items-end"
            style={{
              color: secondaryColor,
              textShadow,
              fontSize: previewLayout.lineFontSize * 0.96,
              lineHeight: 1.12,
            }}
          >
            {bannerConfig.phone && <p className={`max-w-full ${rightContactClass}`} style={{ marginTop: 0 }}>Telefone: {bannerConfig.phone}</p>}
            {bannerConfig.email && <p className={`max-w-full ${rightContactClass}`} style={{ marginTop: rightLineSpacing }}>E-mail: {bannerConfig.email}</p>}
            {bannerConfig.site && <p className={`max-w-full ${rightContactClass}`} style={{ marginTop: rightLineSpacing }}>Site: {bannerConfig.site}</p>}
          </div>
        </div>
      </div>
    </div>
  );

  if (hasBannerImage) {
    if (compact) {
      return (
        <div className="relative aspect-[1600/320] overflow-hidden rounded-[12px] border border-slate-700 bg-[#05111f] shadow-[0_18px_60px_rgba(2,6,23,0.35)]">
          <img
            src={backgroundSrc}
            alt={bannerConfig.title || 'Banner do PDF'}
            className="absolute inset-0 z-0 block h-full w-full object-cover"
          />
          {renderOverlay()}
        </div>
      );
    }

    return (
      <div className="w-full overflow-x-auto overflow-y-hidden rounded-[12px] border border-slate-700 bg-[#05111f] shadow-[0_18px_60px_rgba(2,6,23,0.35)]">
        <div className="relative h-[320px] w-[1600px]">
          <img
            src={backgroundSrc}
            alt={bannerConfig.title || 'Banner do PDF'}
            className="absolute inset-0 z-0 block h-full w-full object-cover"
          />
          {renderOverlay()}
        </div>
      </div>
    );
  }

  if (compact) {
    return (
      <div className="relative aspect-[1600/320] overflow-hidden rounded-[12px] border border-slate-700 bg-gradient-to-r from-slate-950 via-slate-900 to-slate-800 shadow-[0_18px_60px_rgba(2,6,23,0.35)]">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_20%,rgba(34,211,238,0.15),transparent_28%),radial-gradient(circle_at_80%_20%,rgba(96,165,250,0.12),transparent_30%),linear-gradient(135deg,rgba(59,130,246,0.16),transparent_48%)]" />
        {renderOverlay()}
      </div>
    );
  }

  return (
    <div className="w-full overflow-x-auto overflow-y-hidden rounded-[12px] border border-slate-700 bg-gradient-to-r from-slate-950 via-slate-900 to-slate-800 shadow-[0_18px_60px_rgba(2,6,23,0.35)]">
      <div className="relative h-[320px] w-[1600px]">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_20%,rgba(34,211,238,0.15),transparent_28%),radial-gradient(circle_at_80%_20%,rgba(96,165,250,0.12),transparent_30%),linear-gradient(135deg,rgba(59,130,246,0.16),transparent_48%)]" />
        {renderOverlay()}
      </div>
    </div>
  );
};

export default PdfHeaderPreview;
